// Brand color fields — keeps the preset row, the native swatch, and the
// hex text input in step. Targets: [data-brand-field] wrappers holding
// [data-brand-preset] buttons, a [data-brand-swatch] color input, its
// [data-brand-swatch-chip] face, and a [data-brand-hex] text input.
//
// The presets and the swatch are conveniences. The text input is what
// submits, and the server is what validates and derives the readable
// text color, so this file deliberately does no colour maths of its own.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

// A committed hex is the only value the swatch can carry, so the text
// field has to reach that shape before we push it across.
const FULL_HEX = /^#[0-9a-fA-F]{6}$/;

export function initBrandColor(root = document) {
  const fields = root.querySelectorAll("[data-brand-field]");

  fields.forEach((field) => {
    if (!firstBind(field)) return;

    const swatch = field.querySelector("[data-brand-swatch]");
    const chip = field.querySelector("[data-brand-swatch-chip]");
    const hex = field.querySelector("[data-brand-hex]");
    if (!swatch || !hex) return;

    // The chip is what a user sees; the real input is transparent on
    // top of it, so it has to be repainted by hand.
    const paint = (value) => {
      if (chip) chip.style.backgroundColor = value;
    };

    // One place that tells the rest of the page a colour changed, so
    // htmx's debounced preview fires the same way whichever control
    // the user reached for. The hex handler below is the only thing
    // that decides which preset is lit — every path routes through it,
    // so dialling a preset's colour on the OS picker selects it just
    // as typing that hex does.
    const commit = (value) => {
      hex.value = value;
      swatch.value = value;
      paint(value);
      hex.dispatchEvent(new Event("input", { bubbles: true }));
    };

    field.querySelectorAll("[data-brand-preset]").forEach((preset) => {
      preset.addEventListener("click", () =>
        commit(preset.dataset.brandPreset),
      );
    });

    // Dragging the picker fires input continuously; mirror it live so
    // the text field tracks, and let htmx's own debounce decide when to
    // ask the server for a new preview.
    swatch.addEventListener("input", () => {
      hex.value = swatch.value;
      paint(swatch.value);
      hex.dispatchEvent(new Event("input", { bubbles: true }));
    });

    hex.addEventListener("input", () => {
      const typed = hex.value.trim();
      if (!FULL_HEX.test(typed)) return;

      const canonical = typed.toLowerCase();
      swatch.value = canonical;
      paint(canonical);
      markActivePreset(field, matchingPreset(field, canonical));
    });
  });
}

// matchingPreset finds the preset button holding a colour, so reaching
// a preset's hex by any route still lights it up.
function matchingPreset(field, value) {
  return (
    [...field.querySelectorAll("[data-brand-preset]")].find(
      (preset) => preset.dataset.brandPreset.toLowerCase() === value,
    ) || null
  );
}

// markActivePreset mirrors the server-rendered selected ring, so the
// row keeps agreeing with the field between saves.
function markActivePreset(field, active) {
  field.querySelectorAll("[data-brand-preset]").forEach((preset) => {
    const on = preset === active;
    preset.setAttribute("aria-pressed", String(on));
    preset.classList.toggle("ring-2", on);
    preset.classList.toggle("ring-brand-600", on);
    preset.classList.toggle("ring-offset-2", on);
    preset.classList.toggle("ring-offset-white", on);
    preset.classList.toggle("ring-1", !on);
    preset.classList.toggle("ring-black/10", !on);
  });
}
